import React, { useState, useEffect } from 'react';
import classes from './LineGraph.module.css';
import CustomDropdown from '../../UI/CustomDropdown';
import { graphViewOptions } from '../../../data';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area
} from 'recharts';

// Define the options that were previously imported

const LineGraph = ({ options = [], getGraphView, dateRange, initialData }) => {
  const [selectedOption, setSelectedOption] = useState(graphViewOptions[0]);
  const [graphView, setGraphView] = useState(graphViewOptions[0]);

  // Process and sort the data by date
  const processedData = initialData
    .map((item) => ({
      date: new Date(item.data),
      displayDate: item.data,
      value: parseFloat(item.dataValue),
      caption: item.dataCaption
    }))
    .sort((a, b) => a.date - b.date);

  // Get strategic points for x-axis
  const getStrategicPoints = () => {
    const length = processedData.length;
    if (length <= 4) return processedData.map((item) => item.displayDate);

    return [
      processedData[0].displayDate, // Start date
      processedData[Math.floor(length / 3)].displayDate, // One-third point
      processedData[Math.floor((2 * length) / 3)].displayDate, // Two-thirds point
      processedData[length - 1].displayDate // End date
    ];
  };

  // Calculate Y-axis range and ticks
  const getYAxisConfig = () => {
    const values = processedData.map((item) => item.value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);

    // Round up max value to nearest nice number
    const niceMax = Math.ceil(maxValue / 1000) * 1000;
    const step = niceMax / 3;

    return {
      domain: [0, niceMax],
      ticks: [step, step * 2], // Middle lines only
      allTicks: [0, step, step * 2, niceMax] // All tick labels
    };
  };

  // Format date for display
  const formatXAxis = (dateStr) => {
    const date = new Date(dateStr);
    const startDate = processedData[0].date;
    const days = Math.floor((date - startDate) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} Days`;
  };

  const convertIntoISO = (date) => {
    if (!date) return '';
    return new Date(date).toISOString();
  };

  return (
    <div className={classes.wrapper}>
      <div className="flex flex-col gap-4">
        <div className={classes.headerInfo}>
          <h2 className="text-lg font-semibold">Graph View</h2>
          <CustomDropdown
            value={selectedOption}
            setter={(e) => {
              setSelectedOption(e);
              getGraphView({
                // startUtcDate: '2025-01-29T06:47:25.689Z',
                // endUtcDate: '2025-01-29T06:47:25.689Z',
                chartType: e?.value,
                startUtcDate: convertIntoISO(dateRange[0]?.startDate),
                endUtcDate: convertIntoISO(dateRange[0]?.endDate)
              });
            }}
            options={graphViewOptions}
          />
        </div>

        <div className="flex justify-between items-center">
          {options.length > 0 && (
            <div className="flex gap-2">
              {options.map((option) => (
                <button
                  key={option.label}
                  onClick={() => setSelectedOption(option.label)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors
                    ${
                      selectedOption === option.label
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={classes.container}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={processedData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid
                horizontal={true}
                vertical={false}
                stroke="#E5E7EB"
                y={getYAxisConfig().ticks}
              />
              <XAxis
                dataKey="displayDate"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                ticks={getStrategicPoints()}
                tickLine={false}
                axisLine={false}
                padding={{ left: 20, right: 20 }}
              />
              <YAxis
                ticks={getYAxisConfig().allTicks}
                tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={false}
                padding={{ top: 20, bottom: 20 }}
                domain={getYAxisConfig().domain}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-2 shadow-lg rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">{payload[0].payload.caption}</p>
                      </div>
                    );
                  }
                  return null;
                }}
                cursor={{ stroke: 'rgb(79, 70, 229)', strokeWidth: 2 }}
              />
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="rgb(79, 70, 229)" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="rgb(79, 70, 229)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="none"
                fill="url(#colorValue)"
                fillOpacity={1}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="rgb(79, 70, 229)"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 6,
                  style: { fill: 'rgb(79, 70, 229)', stroke: '#fff', strokeWidth: 2 }
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LineGraph;
